import { useRef, useState } from "react";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import { ChangeStepper } from "@/payroll/editor/change-stepper";
import { ARTIFACT_CONTROLS } from "@/payroll/editor/constants";
import { editorError } from "@/payroll/editor/state";
import type { ComparisonTechnician } from "@/payroll/editor/types";
import {
  useComparisonStatement,
  useComparisonStatements,
} from "./comparison-query";
import { StatementPane } from "./statement-pane";
import { StatementsSkeleton } from "./statements-skeleton";
import { useMirroredScroll } from "./use-mirrored-scroll";

const LINK =
  "h-auto min-w-0 p-0 text-sm font-medium text-interactive-text underline-offset-4 hover:underline";

/**
 * One technician's statement, the way they receive it. A statement the proposal
 * cannot render is the loudest thing this view can say, so it says it in place
 * of the document rather than beside it.
 */
export const StatementsView = ({
  path,
  queryKey,
  periodStart,
  enabled,
  note,
}: {
  path: string;
  queryKey: readonly unknown[];
  periodStart: string | undefined;
  enabled: boolean;
  /** Why there is nothing to show, decided once for both lenses. */
  note: string;
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [changedOnly, setChangedOnly] = useState(true);
  const rail = useRef<HTMLUListElement>(null);
  const list = useComparisonStatements(path, queryKey, periodStart, enabled);

  const technicians = list.data?.technicians ?? [];
  const changed = technicians.filter((technician) => technician.changed);
  const listed = changedOnly && changed.length ? changed : technicians;
  const active = listed.some((entry) => entry.id === selected)
    ? selected
    : (listed[0]?.id ?? null);
  const detail = useComparisonStatement(path, queryKey, periodStart, active);
  // The same lock the grid uses. Two statements read side by side are only a
  // comparison while they are showing the same part of the document, and these
  // run to a screen or more each: scrolling one and not the other puts a
  // technician's bonus beside somebody's hours.
  const { currentRef, proposedRef } = useMirroredScroll(detail.data);
  // Walking the PayPacks that moved, the way every other pane walks its
  // changes. Without it the only way through 333 people was to read the rail.
  const at = changed.findIndex((entry) => entry.id === active);
  const goTo = (by: number) => {
    if (!changed.length) return;
    const next =
      at < 0
        ? by > 0
          ? 0
          : changed.length - 1
        : (at + by + changed.length) % changed.length;
    setSelected(changed[next].id);
    const node = rail.current?.querySelector(
      `[data-technician="${CSS.escape(changed[next].id)}"]`,
    );
    const viewport = rail.current?.closest<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!node || !viewport) return;
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  };

  // Said by the view that knows which of the four reasons applies. This lens
  // used to tell a reader watching a run in progress to go and start one.
  if (!enabled) return <p className="p-4 text-secondary-text">{note}</p>;
  if (list.isPending) return <StatementsSkeleton />;
  if (list.isError)
    return (
      <p className="p-4" role="alert">
        {editorError(list.error)}
      </p>
    );
  if (!list.data?.available)
    return <p className="p-4 text-secondary-text">{note}</p>;
  if (!technicians.length)
    return (
      <p className="p-4 text-secondary-text">
        This pay period produced no PayPacks.
      </p>
    );

  return (
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* The chrome's own band, matching the rows the other panes carry: the
          same grey ground, the same 40px floor. It was white, which read as the
          first line of the PayPack rather than as a control over it. */}
      <div
        className={cn(
          ARTIFACT_CONTROLS,
          "flex-wrap gap-x-3 gap-y-1 border-b-2",
        )}
      >
        {changed.length > 1 && (
          <ChangeStepper
            noun="change"
            label={
              at < 0
                ? `${changed.length} changes`
                : `Change ${at + 1} of ${changed.length}`
            }
            onStep={goTo}
          />
        )}
        <span className="text-secondary-text">
          {changed.length} of {technicians.length} PayPacks changed
        </span>
        {!!changed.length && changed.length < technicians.length && (
          <Button
            size="xs"
            variant="text"
            className={LINK}
            onClick={() => {
              setChangedOnly(!changedOnly);
              setSelected(null);
            }}
          >
            {changedOnly
              ? `Show all ${technicians.length}`
              : "Only the ones that changed"}
          </Button>
        )}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-[13rem_1fr]">
        <ScrollArea
          vertical
          className="min-h-0 border-r border-stroke-tertiary"
        >
          <ul ref={rail} className="py-1">
            {listed.map((technician) => (
              <li key={technician.id} data-technician={technician.id}>
                <button
                  type="button"
                  onClick={() => setSelected(technician.id)}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm",
                    {
                      "bg-surface-secondary font-medium":
                        technician.id === active,
                      "text-secondary-text hover:text-primary-text": !(
                        technician.id === active
                      ),
                    },
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {technician.name}
                  </span>
                  {!technician.proposed_renders ? (
                    <span className="shrink-0 text-sm uppercase tracking-wide">
                      Broken
                    </span>
                  ) : (
                    technician.changed && (
                      <span
                        aria-label="Changed"
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-stroke-interactive"
                      />
                    )
                  )}
                </button>
              </li>
            ))}
          </ul>
        </ScrollArea>
        {detail.isPending && <StatementsSkeleton body />}
        {detail.isError && (
          <p className="p-4" role="alert">
            {editorError(detail.error)}
          </p>
        )}
        {!!detail.data && (
          <div className="grid min-h-0 grid-cols-2 divide-x divide-stroke-tertiary">
            <StatementPane
              heading="Today's rules"
              side={detail.data.current}
              viewportRef={currentRef}
            />
            <StatementPane
              heading="Your proposal"
              side={detail.data.proposed}
              viewportRef={proposedRef}
            />
          </div>
        )}
      </div>
    </Stack>
  );
};

export type { ComparisonTechnician };
